import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from '@hsc/auth';
import {
  setAuthTokenProvider,
  setTenantSlugProvider,
  setRefreshHandler,
  setOnAuthFailure,
} from '@hsc/api-client';
import { TenantProvider } from '@hsc/tenant';
import type { Tenant, TenantId } from '@hsc/types';

import {
  ACCESS_TOKEN_KEY,
  readAccessTokenCookie,
  readLocalAccessToken,
  readLocalRefreshToken,
  writeLocalAccessToken,
  writeLocalRefreshToken,
  clearLocalTokens,
} from './lib/storage.js';
import { refreshSession } from './shared/api/auth.js';
import { resolveTenantSlug } from './lib/tenant-slug.js';
import { LoginRoute } from './routes/login.js';
import { Workspace } from './routes/Workspace.js';
import { StaffPage } from './features/staff/StaffPage.js';
import { StaffDetailPage } from './features/staff/StaffDetailPage.js';
import { StaffInvitePage } from './features/staff/StaffInvitePage.js';
import { SettingsRoute } from './features/staff/SettingsRoute.js';
import { CatalogPage } from './features/catalog/CatalogPage.js';
import { CategoryServicesPage } from './features/catalog/CategoryServicesPage.js';
import { RequestNewPage } from './features/catalog/RequestNewPage.js';
import { MaintenanceReportPage } from './features/maintenance/MaintenanceReportPage.js';
import {
  RoomServiceRequestPage,
  IN_ROOM_CONFIG,
  REPAIR_CONFIG,
} from './features/guest-home/RoomServiceRequestPage.js';
import { OtherRequestPage } from './features/guest-home/OtherRequestPage.js';
import { RequestsByRole } from './features/requests/RequestsByRole.js';
import { RequestDetailPage } from './features/requests/RequestDetailPage.js';
import { RequestsAnalyticsPage } from './features/requests-analytics/RequestsAnalyticsPage.js';
import { AnonymousMode } from './features/anonymous/AnonymousMode.js';
import { FnbQueuePage } from './features/fnb-queue/FnbQueuePage.js';
import { BillingByRole } from './features/billing/BillingByRole.js';
import { MyStayPage } from './features/guest-stay/MyStayPage.js';
import { SosPage } from './features/sos/SosPage.js';
import { GrantAccessPage } from './features/guest-access/GrantAccessPage.js';
import { PendingApprovalsPage } from './features/guest-access/PendingApprovalsPage.js';
import { GuestAccessHubPage } from './features/guest-access/GuestAccessHubPage.js';
import { AccessListPage } from './features/guest-access/AccessListPage.js';
import { GuestSelfAuthRoute } from './routes/guest-self-auth.js';
import { RequireManagementRole, RequireFrontDeskRole } from './shared/nav/RequireManagementRole.js';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

/**
 * Мост токена/tenant: подключает провайдеры `@hsc/api-client` к локальному
 * хранилищу токена и к резолверу slug (поддомен → env-фоллбэк).
 */
function ApiBridge(): null {
  const { token } = useAuth();
  useEffect(() => {
    setAuthTokenProvider(() => readLocalAccessToken() ?? token ?? readAccessTokenCookie());
    setTenantSlugProvider(() => resolveTenantSlug());
    setRefreshHandler(async () => {
      const rt = readLocalRefreshToken();
      if (!rt) return false;
      try {
        const r = await refreshSession(rt);
        if (r.access_token) {
          writeLocalAccessToken(r.access_token);
          if (r.refresh_token) writeLocalRefreshToken(r.refresh_token);
          return true;
        }
      } catch {
        /* refresh не удался — упадём на onAuthFailure */
      }
      return false;
    });
    setOnAuthFailure(() => {
      clearLocalTokens();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    });
  }, [token]);
  return null;
}

/** Минимальный фетчер tenant — реальные данные приходят из /v1/platform/me. */
function tenantFetcher(slug: string): Promise<Tenant> {
  return Promise.resolve({ id: slug as TenantId, slug, displayName: slug });
}

/** Защита роутов: неавторизованного отправляем на /login. */
export function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const hasLooseToken = Boolean(readLocalAccessToken() ?? readAccessTokenCookie());
  if (!isAuthenticated && !hasLooseToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

/**
 * Обёртка Workspace, извлекающая активный ключ модуля из роута.
 *
 * Модули из /v1/platform/me могут иметь МНОГОСЕГМЕНТНЫЕ пути
 * (`/admin/subscription`, `/me/notification-preferences`), поэтому ключ
 * берём из полного pathname: последний непустой сегмент. Это чинит F-TN-NAV
 * — раньше такие пункты не совпадали с одиночным `/:section` и уходили в
 * catch-all `*` → редирект на /login (сайдбар пропадал, клики падали).
 */
function ModuleWorkspace(): JSX.Element {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);
  const activeKey = segments.length > 0 ? segments[segments.length - 1] : undefined;
  return <Workspace {...(activeKey ? { activeKey } : {})} />;
}

export function App(): JSX.Element {
  const initialToken = useMemo(() => readLocalAccessToken() ?? readAccessTokenCookie(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider storageKey={ACCESS_TOKEN_KEY} initialToken={initialToken}>
        <TenantProvider fetcher={tenantFetcher}>
          <ApiBridge />
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginRoute />} />
              {/* Анон-режим витрины отеля (#323/#12) — БЕЗ RequireAuth,
                  тенант по поддомену, JWT не требуется. */}
              <Route path="/anonymous" element={<AnonymousMode />} />
              {/* «Я гость отеля» — само-авторизация гостя (Phase 3 гост-входа).
                  Отдельный экран со страницы входа; анон, tenant по поддомену. */}
              <Route path="/guest-login" element={<GuestSelfAuthRoute />} />
              {/* Корень и все дашборды роли + модули рендерятся одним
                  Workspace, который сам резолвит роль из /v1/platform/me. */}
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <Workspace />
                  </RequireAuth>
                }
              />
              {/* Каталог услуг — реальные роуты витрины (canon design-guest
                  §15/§16): /services/{categoryCode} → /requests/new?service=…
                  Deep-link и BackButton работают независимо от modules[]. */}
              <Route
                path="/services"
                element={
                  <RequireAuth>
                    <Workspace page={<CatalogPage />} />
                  </RequireAuth>
                }
              />
              <Route
                path="/services/:categoryCode"
                element={
                  <RequireAuth>
                    <Workspace page={<CategoryServicesPage />} selfBack />
                  </RequireAuth>
                }
              />
              <Route
                path="/requests/new"
                element={
                  <RequireAuth>
                    <Workspace page={<RequestNewPage />} selfBack />
                  </RequireAuth>
                }
              />
              {/* «Облака» #31 — поломка в номере: заявка категории
                  maintenance с фото (presign). Deep-link /maintenance. */}
              <Route
                path="/maintenance"
                element={
                  <RequireAuth>
                    <Workspace page={<MaintenanceReportPage />} />
                  </RequireAuth>
                }
              />
              {/* Гостевой дом (#79): «В номер» (housekeeping) / «Ремонт»
                  (maintenance) — чек-лист → одна заявка отдела; «Другое» —
                  свободная заявка. Отдельные экраны с «Назад». */}
              <Route
                path="/in-room"
                element={
                  <RequireAuth>
                    <Workspace page={<RoomServiceRequestPage config={IN_ROOM_CONFIG} />} selfBack />
                  </RequireAuth>
                }
              />
              <Route
                path="/repair"
                element={
                  <RequireAuth>
                    <Workspace page={<RoomServiceRequestPage config={REPAIR_CONFIG} />} selfBack />
                  </RequireAuth>
                }
              />
              <Route
                path="/other"
                element={
                  <RequireAuth>
                    <Workspace page={<OtherRequestPage />} selfBack />
                  </RequireAuth>
                }
              />
              {/* Заявки гостя (UC-G.06/07/08): список «Мои заявки» +
                  карточка с трекингом статуса и чатом. Объявлены ДО
                  /:section, иначе /requests ушёл бы в module-резолв. */}
              <Route
                path="/requests"
                element={
                  <RequireAuth>
                    <Workspace activeKey="requests" page={<RequestsByRole />} />
                  </RequireAuth>
                }
              />
              {/* §6.3 «Аналитика заявок» — статический путь ДО /requests/:id,
                  иначе «analytics» ушёл бы в RequestDetailPage как id.
                  activeKey="requests-analytics" — подсветка пункта nav. */}
              <Route
                path="/requests/analytics"
                element={
                  <RequireAuth>
                    <Workspace activeKey="requests-analytics" page={<RequestsAnalyticsPage />} />
                  </RequireAuth>
                }
              />
              <Route
                path="/requests/:id"
                element={
                  <RequireAuth>
                    <Workspace activeKey="requests" page={<RequestDetailPage />} />
                  </RequireAuth>
                }
              />
              {/* Меню F&B + заказ в номер (UC-G.09) и счёт/фолио гостя
                  (UC-G.13) — явные роуты, работают независимо от modules[]. */}
              <Route
                path="/menu"
                element={
                  <RequireAuth>
                    {/* activeKey "menu" → Workspace резолвит MenuPage с canManage:
                        менеджер/владелец видят обзор меню (§9.1), гость — заказ. */}
                    <Workspace activeKey="menu" />
                  </RequireAuth>
                }
              />
              {/* Менеджерские подэкраны меню (design §9): категории (9.2) и
                  позиции (9.3). MenuPage сам резолвит экран по подпути и роли;
                  activeKey="menu" — для подсветки навигации. */}
              <Route
                path="/menu/categories"
                element={
                  <RequireAuth>
                    <Workspace activeKey="menu" selfBack />
                  </RequireAuth>
                }
              />
              <Route
                path="/menu/items"
                element={
                  <RequireAuth>
                    <Workspace activeKey="menu" selfBack />
                  </RequireAuth>
                }
              />
              {/* Заказы (live, §9.4/9.5) — та же очередь F&B, что и /fnb-queue
                  (kanban new→cooking→ready). Отдельный путь из обзора меню. */}
              <Route
                path="/menu/orders"
                element={
                  <RequireAuth>
                    <Workspace activeKey="menu" page={<FnbQueuePage />} />
                  </RequireAuth>
                }
              />
              {/* Гостевое меню (design-guest v7): §11 категория /menu/c/{code}
                  и §12 корзина /menu/order. MenuPage резолвит экран по подпути;
                  у менеджера эти подпути не используются (он на §9). */}
              <Route
                path="/menu/c/:categoryCode"
                element={
                  <RequireAuth>
                    <Workspace activeKey="menu" selfBack />
                  </RequireAuth>
                }
              />
              <Route
                path="/menu/order"
                element={
                  <RequireAuth>
                    <Workspace activeKey="menu" selfBack />
                  </RequireAuth>
                }
              />
              {/* Очередь заказов F&B персоналу (кухня/бар/официант) —
                  блок «Облака». activeKey для подсветки навигации. */}
              <Route
                path="/fnb-queue"
                element={
                  <RequireAuth>
                    <Workspace activeKey="fnb-queue" page={<FnbQueuePage />} />
                  </RequireAuth>
                }
              />
              <Route
                path="/folio"
                element={
                  <RequireAuth>
                    <Workspace page={<BillingByRole />} />
                  </RequireAuth>
                }
              />
              {/* Гость: «Моё проживание» (UC-G, design-guest §7) — stay-card,
                  продление, поздний выезд, детали номера. Явный роут, чтобы
                  deep-link работал независимо от modules[]. */}
              <Route
                path="/my-stay"
                element={
                  <RequireAuth>
                    <Workspace activeKey="my-stay" page={<MyStayPage />} />
                  </RequireAuth>
                }
              />
              {/* SOS (P0, design-guest §15.4) — экстренная заявка персоналу. */}
              <Route
                path="/sos"
                element={
                  <RequireAuth>
                    <Workspace activeKey="sos" page={<SosPage />} />
                  </RequireAuth>
                }
              />
              {/* «Все счета» / счета — псевдоним маршрута модуля (#338). */}
              <Route
                path="/billing"
                element={
                  <RequireAuth>
                    <RequireManagementRole>
                      <Workspace page={<BillingByRole />} />
                    </RequireManagementRole>
                  </RequireAuth>
                }
              />
              {/* Любой прочий авторизованный путь (вкл. многосегментные
                  модули `/admin/subscription`, `/me/notification-preferences`)
                  рендерит Workspace; ключ модуля — последний сегмент пути.
                  Неавторизованного RequireAuth сам уводит на /login. */}
              {/* §7.3 Пригласить сотрудника — отдельный роут (deep-link). */}
              <Route
                path="/staff/invite"
                element={
                  <RequireAuth>
                    <RequireManagementRole>
                      <Workspace activeKey="staff" page={<StaffInvitePage />} selfBack />
                    </RequireManagementRole>
                  </RequireAuth>
                }
              />
              {/* §7.2 Карточка сотрудника /staff/{id}. */}
              <Route
                path="/staff/:id"
                element={
                  <RequireAuth>
                    <RequireManagementRole>
                      <Workspace activeKey="staff" page={<StaffDetailPage />} selfBack />
                    </RequireManagementRole>
                  </RequireAuth>
                }
              />
              {/* §7.1 Персонал. */}
              <Route
                path="/staff"
                element={
                  <RequireAuth>
                    <RequireManagementRole>
                      <Workspace activeKey="staff" page={<StaffPage />} />
                    </RequireManagementRole>
                  </RequireAuth>
                }
              />
              {/* Раздел «Гостевой доступ» (Phase 3): хаб с 3 экранами
                  (owner/manager/front_desk). Под-маршруты объявлены ДО хаба и
                  ДО /:section/catch-all, чтобы не уходили в module-резолв.
                  Каждый пункт — отдельный экран с «Назад» в хаб. */}
              <Route
                path="/guest-access/pending"
                element={
                  <RequireAuth>
                    {/* owner/manager + front_desk (админ/ресепшен) — бэк пускает
                        front_desk на approve. */}
                    <RequireFrontDeskRole>
                      <Workspace activeKey="guest-access" page={<PendingApprovalsPage />} selfBack />
                    </RequireFrontDeskRole>
                  </RequireAuth>
                }
              />
              <Route
                path="/guest-access/grant"
                element={
                  <RequireAuth>
                    <RequireFrontDeskRole>
                      <Workspace activeKey="guest-access" page={<GrantAccessPage backTo="/guest-access" />} selfBack />
                    </RequireFrontDeskRole>
                  </RequireAuth>
                }
              />
              <Route
                path="/guest-access/list"
                element={
                  <RequireAuth>
                    <RequireFrontDeskRole>
                      <Workspace activeKey="guest-access" page={<AccessListPage />} selfBack />
                    </RequireFrontDeskRole>
                  </RequireAuth>
                }
              />
              <Route
                path="/guest-access"
                element={
                  <RequireAuth>
                    <RequireFrontDeskRole>
                      <Workspace activeKey="guest-access" page={<GuestAccessHubPage />} selfBack />
                    </RequireFrontDeskRole>
                  </RequireAuth>
                }
              />
              {/* Настройки → вкладки Отделы/Роли (deep-link). */}
              <Route
                path="/settings/:tab"
                element={
                  <RequireAuth>
                    <RequireManagementRole>
                      <Workspace activeKey="settings" page={<SettingsRoute />} />
                    </RequireManagementRole>
                  </RequireAuth>
                }
              />
              <Route
                path="*"
                element={
                  <RequireAuth>
                    <ModuleWorkspace />
                  </RequireAuth>
                }
              />
            </Routes>
          </BrowserRouter>
        </TenantProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
